import { generateWindowFromReelStrips, isNumeric } from '../../helpers';
import { ICoin } from '../ff001-starstruck-lines/interfaces';
import { copyWindow, TCoinWindow } from '../ff001-starstruck-lines/coin-spins/helpers';

export type TFf035Scenario = {
  reelIndexes?: number[];
  reelSet?: 'freespins';
  coinSpin?: TCoinWindow;
  forceCoinSpinsTrigger?: true;
};

export type TFf035EditablePlay = {
  reelIndexes: number[];
  reelSet?: 'freespins';
  coinSpins?: TCoinWindow[];
  forceCoinSpinsTrigger?: true;
};

export const getVisibleWindow = (
  reelIndexes: number[],
  config: any,
  reelSet?: TFf035EditablePlay['reelSet'],
): string[][] => generateWindowFromReelStrips({
  reelIndexes,
  reelStrips: reelSet === 'freespins' ? config.freeSpinsReels : config.baseSpinsReels,
  reels: config.reelsSetup.length,
  rows: config.reelsSetup[0],
});

export const countCoins = (window: string[][], config: any): number =>
  window.reduce(
    (count, reel) => count + reel.filter(symbol => symbol === config.coin).length,
    0,
  );

export const isCoinSpinsEligible = (window: string[][], config: any): boolean =>
  countCoins(window, config) >= config.coinFeature.terminalCoinTriggerMinimumCoins;

export const getConfiguredCoins = (config: any): Array<{ name: string; coin: ICoin }> =>
  config.coinFeature.coins.map((configuredCoin: number | string) => {
    const priceCoin = isNumeric(configuredCoin);
    const coinType = priceCoin ? 'price' : configuredCoin;
    const multiplier = priceCoin ? Number(configuredCoin) : 0;
    return {
      name: `${configuredCoin}`,
      coin: {
        startWindowIndex: 0,
        multiplier,
        totalWinInCredits: 0,
        enabled: true,
        coinType,
        history: [{ type: coinType, multiplier, totalWinInCredits: 0 }],
      } as ICoin,
    };
  });

export const seedCoinSpinWindows = (
  visibleWindow: string[][],
  config: any,
): TCoinWindow[] => {
  const configuredCoin = getConfiguredCoins(config)[0]?.coin;
  if (!configuredCoin) {
    throw new Error('FF035 Coin Spins requires at least one configured coin');
  }
  const initialWindow = visibleWindow.map(reel => reel.map(symbol =>
    symbol === config.coin ? { ...configuredCoin } : null));
  return Array.from(
    { length: config.coinFeature.coinTriggerSpinCount + 1 },
    () => copyWindow(initialWindow),
  );
};

export const reconcileCoinSpinsEligibility = (
  play: TFf035EditablePlay,
  config: any,
): TFf035EditablePlay => {
  const window = getVisibleWindow(play.reelIndexes, config, play.reelSet);
  if (isCoinSpinsEligible(window, config)) return play;
  const {
    coinSpins: _removedCoinSpins,
    forceCoinSpinsTrigger: _removedForce,
    ...ordinaryPlay
  } = play;
  return ordinaryPlay;
};

export const toNativeScenarioQueue = (
  plays: TFf035EditablePlay[],
): TFf035Scenario[] => plays.flatMap(play => {
  const trigger: TFf035Scenario = { reelIndexes: [...play.reelIndexes] };
  if (play.reelSet === 'freespins') {
    trigger.reelSet = 'freespins';
  }
  if (!play.coinSpins?.length) return [trigger];
  trigger.coinSpin = copyWindow(play.coinSpins[0]);
  if (play.forceCoinSpinsTrigger === true) {
    trigger.forceCoinSpinsTrigger = true;
  }
  return [
    trigger,
    ...play.coinSpins.slice(1).map(coinSpin => ({ coinSpin: copyWindow(coinSpin) })),
  ];
});

export const fromNativeScenarioQueue = (
  scenarios: TFf035Scenario[],
): TFf035EditablePlay[] => {
  const plays: TFf035EditablePlay[] = [];
  scenarios.forEach(scenario => {
    if (scenario.reelIndexes) {
      plays.push({
        reelIndexes: [...scenario.reelIndexes],
        ...(scenario.reelSet === 'freespins' ? { reelSet: 'freespins' as const } : {}),
        ...(scenario.coinSpin ? { coinSpins: [copyWindow(scenario.coinSpin)] } : {}),
        ...(scenario.forceCoinSpinsTrigger === true
          ? { forceCoinSpinsTrigger: true as const }
          : {}),
      });
      return;
    }
    if (scenario.coinSpin && plays[plays.length - 1]?.coinSpins) {
      plays[plays.length - 1].coinSpins!.push(copyWindow(scenario.coinSpin));
    }
  });
  return plays;
};

export const getScenarioLabel = (
  plays: TFf035EditablePlay[],
  config: any,
): string => {
  const coinCount = plays.reduce(
    (total, play) =>
      total + countCoins(getVisibleWindow(play.reelIndexes, config, play.reelSet), config),
    0,
  );
  const coinSpinSteps = plays.reduce(
    (total, play) => total + (play.coinSpins?.length ?? 0),
    0,
  );
  return `${plays.length} play(s), ${coinCount} coin(s), ${coinSpinSteps} Coin Spin step(s)`;
};
